import * as React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { LazyBreadcrumb, LazyBreadcrumbsProps } from '../LazyBreadcrumb';

/**
 * Configuration d'un élément du fil d'Ariane
 */
export type BreadcrumbItem = string | { path: string; link: string };

/**
 * Props du composant ListHeader
 */
export interface ListHeaderProps {
    /**
     * Chemin du fil d'Ariane à afficher
     * Peut contenir des chaînes simples ou des objets avec path et link
     */
    breadcrumbPath: LazyBreadcrumbsProps['path'];

    /**
     * Éléments d'action à afficher à droite du header (boutons, etc.)
     * Utiliser React.ReactNode pour une flexibilité maximale
     */
    actions?: React.ReactNode;

    /**
     * Styles additionnels à appliquer au conteneur principal
     */
    sx?: SxProps<Theme>;
}

/**
 * Composant d'en-tête générique pour les pages de listing.
 *
 * Fournit une structure uniforme comprenant :
 * - Un fil d'Ariane (breadcrumb) à gauche
 * - Une zone d'actions à droite (boutons de création, filtres, etc.)
 *
 * @example
 * ```tsx
 * <ListHeader
 *     breadcrumbPath={[
 *         { path: 'Prescripteurs', link: '/prescripteurs/liste' },
 *         'Recherche',
 *         'Liste des prescripteurs',
 *     ]}
 *     actions={
 *         <>
 *             <HeaderActionButton
 *                 label="Créer un prescripteur"
 *                 icon={AddIcon}
 *                 onClick={handleCreate}
 *             />
 *         </>
 *     }
 * />
 * ```
 */
const ListHeader: React.FunctionComponent<ListHeaderProps> = function ({
    breadcrumbPath,
    actions,
    sx,
}: ListHeaderProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
                ...sx,
            }}
        >
            <LazyBreadcrumb path={breadcrumbPath} />

            {actions && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {actions}
                </Box>
            )}
        </Box>
    );
};

export default ListHeader;
